'use client';

import { c as _c } from "react/compiler-runtime";
import React, { Component, ErrorInfo, ReactNode, useState } from 'react';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { Session } from 'next-auth';
import logger from '@spa/lib/logger';
import styles from './global-error-debug.component.module.scss';
import useFetch from '@spa/lib/react/hooks/useFetch';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
interface BoundaryProps {
  readonly children: ReactNode;
}
interface BoundaryState {
  hasError: boolean;
  error: string | null;
}
export interface NextError extends Error {
  digest?: string;
}
interface Props {
  readonly error: NextError;
  readonly locale: string;
}
const FETCH_OPTIONS: RequestInit = {
  credentials: 'same-origin'
};
class GlobalErrorDebugBoundary extends Component<BoundaryProps, BoundaryState> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(): {
    hasError: boolean;
  } {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error({
      err: error,
      errorInfo
    }, 'DebugErrorBoundary caught an error');
    this.setState({
      hasError: true,
      error: error.toString()
    });
  }
  render(): ReactNode {
    const {
      hasError
    }: BoundaryState = this.state;
    const {
      children
    }: BoundaryProps = this.props;
    return hasError ? <div>{'Global Error debug information could not be loaded.'}</div> : children;
  }
}
const findCookieStrByKey = (key: string): string | undefined => document.cookie.split('; ').find(row => row.startsWith(key))?.split('=')[1];
const getCartIdsFromCookie = (): string => {
  let result: string = '-';
  const cookieStr = findCookieStrByKey('CARTS_COOKIE');
  if (cookieStr) {
    const parsed = JSON.parse(decodeURIComponent(cookieStr));
    result = Object.entries(parsed).map(([type, value]) => {
      const entry = value as {
        cartId: string | null;
      };
      const id: string = entry?.cartId ?? '-';
      return `${type}: ${id}`;
    }).join(', ');
  }
  return result;
};
const getUserIdFromCookie = (): string => {
  let result: string = '-';
  const cookieStr = findCookieStrByKey('session');
  if (cookieStr) {
    const parsed = JSON.parse(decodeURIComponent(cookieStr));
    result = parsed.guid;
  }
  return result;
};
export default function GlobalErrorDebugComponent(t0) {
  const $ = _c(144);
  const {
    error: t1,
    locale
  } = t0;
  const {
    digest,
    message,
    stack
  } = t1;
  const {
    formatMessage
  } = useNamespacedIntl(`${locale}`);
  const [copied, setCopied] = useState(false);
  const [date] = useState(_temp);
  const fetchResult = useFetch("/api/auth/session", FETCH_OPTIONS);
  let sessionStatus;
  bb0: switch (fetchResult.type) {
    case "pending":
    case "in-progress":
      {
        sessionStatus = "loading";
        break bb0;
      }
    case "succeeded":
      {
        sessionStatus = fetchResult.data && Object.keys(fetchResult.data).length > 0 ? "authenticated" : "unauthenticated";
        break bb0;
      }
    case "failed":
      {
        sessionStatus = "unauthenticated";
      }
  }
  const isBrowser = typeof window !== "undefined";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = isBrowser ? getUserIdFromCookie() : "-";
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const guid = t2;
  const anonymousUserId = sessionStatus === "unauthenticated" ? guid : "-";
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = isBrowser ? getCartIdsFromCookie() : "-";
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const cartIds = t3;
  const domain = isBrowser ? window.location.hostname : "-";
  const path = isBrowser ? window.location.pathname : "-";
  const userAgent = isBrowser ? window.navigator.userAgent : "-";
  const userId = sessionStatus === "authenticated" ? guid : "-";
  const details = stack ? String(stack) : message ? String(message) : "-";
  let t4;
  if ($[2] !== formatMessage) {
    t4 = formatMessage("details");
    $[2] = formatMessage;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = `${t4}: ${details}`;
  let t6;
  if ($[4] !== formatMessage) {
    t6 = formatMessage("digest");
    $[4] = formatMessage;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = `${t6}: ${digest}`;
  let t8;
  if ($[6] !== formatMessage) {
    t8 = formatMessage("domain");
    $[6] = formatMessage;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = `${t8}: ${domain}`;
  let t10;
  if ($[8] !== formatMessage) {
    t10 = formatMessage("path");
    $[8] = formatMessage;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = `${t10}: ${path}`;
  let t12;
  if ($[10] !== formatMessage) {
    t12 = formatMessage("date");
    $[10] = formatMessage;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const t13 = `${t12}: ${date}`;
  let t14;
  if ($[12] !== formatMessage) {
    t14 = formatMessage("userAgent");
    $[12] = formatMessage;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const t15 = `${t14}: ${userAgent}`;
  let t16;
  if ($[14] !== formatMessage) {
    t16 = formatMessage("cartIds");
    $[14] = formatMessage;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  const t17 = `${t16}: ${cartIds}`;
  let t18;
  if ($[16] !== formatMessage) {
    t18 = formatMessage("userId");
    $[16] = formatMessage;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  const t19 = `${t18}: ${userId}`;
  let t20;
  if ($[18] !== formatMessage) {
    t20 = formatMessage("anonymousId");
    $[18] = formatMessage;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  const t21 = `${t20}: ${anonymousUserId}`;
  let t22;
  if ($[20] !== t11 || $[21] !== t13 || $[22] !== t15 || $[23] !== t17 || $[24] !== t19 || $[25] !== t21 || $[26] !== t5 || $[27] !== t7 || $[28] !== t9) {
    t22 = [t5, t7, t9, t11, t13, t15, t17, t19, t21];
    $[20] = t11;
    $[21] = t13;
    $[22] = t15;
    $[23] = t17;
    $[24] = t19;
    $[25] = t21;
    $[26] = t5;
    $[27] = t7;
    $[28] = t9;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const debugPayload = t22.join("\n");
  let t23;
  if ($[30] !== debugPayload) {
    t23 = async () => {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(debugPayload);
        setCopied(true);
      }
      setTimeout(() => setCopied(false), 3000);
    };
    $[30] = debugPayload;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  const handleCopyAll = t23;
  let t24;
  if ($[32] !== formatMessage) {
    t24 = formatMessage("errorDetails");
    $[32] = formatMessage;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== t24) {
    t25 = <summary>{t24}</summary>;
    $[34] = t24;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] !== formatMessage) {
    t26 = formatMessage("attribute");
    $[36] = formatMessage;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== t26) {
    t27 = <th scope="col">{t26}</th>;
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== formatMessage) {
    t28 = formatMessage("value");
    $[40] = formatMessage;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] !== t28) {
    t29 = <th scope="col">{t28}</th>;
    $[42] = t28;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== t27 || $[45] !== t29) {
    t30 = <thead><tr>{t27}{t29}</tr></thead>;
    $[44] = t27;
    $[45] = t29;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] !== formatMessage) {
    t31 = formatMessage("details");
    $[47] = formatMessage;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t31) {
    t32 = <th>{t31}</th>;
    $[49] = t31;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== details) {
    t33 = <td>{details}</td>;
    $[51] = details;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== t32 || $[54] !== t33) {
    t34 = <tr>{t32}{t33}</tr>;
    $[53] = t32;
    $[54] = t33;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== formatMessage) {
    t35 = formatMessage("digest");
    $[56] = formatMessage;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== t35) {
    t36 = <th>{t35}</th>;
    $[58] = t35;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== digest) {
    t37 = <td>{digest}</td>;
    $[60] = digest;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== t36 || $[63] !== t37) {
    t38 = <tr>{t36}{t37}</tr>;
    $[62] = t36;
    $[63] = t37;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  let t39;
  if ($[65] !== formatMessage) {
    t39 = formatMessage("domain");
    $[65] = formatMessage;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] !== t39) {
    t40 = <th>{t39}</th>;
    $[67] = t39;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <td>{domain}</td>;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t40) {
    t42 = <tr>{t40}{t41}</tr>;
    $[70] = t40;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== formatMessage) {
    t43 = formatMessage("path");
    $[72] = formatMessage;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  let t44;
  if ($[74] !== t43) {
    t44 = <th>{t43}</th>;
    $[74] = t43;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <td>{path}</td>;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== t44) {
    t46 = <tr>{t44}{t45}</tr>;
    $[77] = t44;
    $[78] = t46;
  } else {
    t46 = $[78];
  }
  let t47;
  if ($[79] !== formatMessage) {
    t47 = formatMessage("date");
    $[79] = formatMessage;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  let t48;
  if ($[81] !== t47) {
    t48 = <th>{t47}</th>;
    $[81] = t47;
    $[82] = t48;
  } else {
    t48 = $[82];
  }
  let t49;
  if ($[83] !== date) {
    t49 = <td>{date}</td>;
    $[83] = date;
    $[84] = t49;
  } else {
    t49 = $[84];
  }
  let t50;
  if ($[85] !== t48 || $[86] !== t49) {
    t50 = <tr>{t48}{t49}</tr>;
    $[85] = t48;
    $[86] = t49;
    $[87] = t50;
  } else {
    t50 = $[87];
  }
  let t51;
  if ($[88] !== formatMessage) {
    t51 = formatMessage("userAgent");
    $[88] = formatMessage;
    $[89] = t51;
  } else {
    t51 = $[89];
  }
  let t52;
  if ($[90] !== t51) {
    t52 = <th>{t51}</th>;
    $[90] = t51;
    $[91] = t52;
  } else {
    t52 = $[91];
  }
  let t53;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <td>{userAgent}</td>;
    $[92] = t53;
  } else {
    t53 = $[92];
  }
  let t54;
  if ($[93] !== t52) {
    t54 = <tr>{t52}{t53}</tr>;
    $[93] = t52;
    $[94] = t54;
  } else {
    t54 = $[94];
  }
  let t55;
  if ($[95] !== formatMessage) {
    t55 = formatMessage("cartIds");
    $[95] = formatMessage;
    $[96] = t55;
  } else {
    t55 = $[96];
  }
  let t56;
  if ($[97] !== t55) {
    t56 = <th>{t55}</th>;
    $[97] = t55;
    $[98] = t56;
  } else {
    t56 = $[98];
  }
  let t57;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <td>{cartIds}</td>;
    $[99] = t57;
  } else {
    t57 = $[99];
  }
  let t58;
  if ($[100] !== t56) {
    t58 = <tr>{t56}{t57}</tr>;
    $[100] = t56;
    $[101] = t58;
  } else {
    t58 = $[101];
  }
  let t59;
  if ($[102] !== formatMessage) {
    t59 = formatMessage("userId");
    $[102] = formatMessage;
    $[103] = t59;
  } else {
    t59 = $[103];
  }
  let t60;
  if ($[104] !== t59) {
    t60 = <th>{t59}</th>;
    $[104] = t59;
    $[105] = t60;
  } else {
    t60 = $[105];
  }
  let t61;
  if ($[106] !== userId) {
    t61 = <td>{userId}</td>;
    $[106] = userId;
    $[107] = t61;
  } else {
    t61 = $[107];
  }
  let t62;
  if ($[108] !== t60 || $[109] !== t61) {
    t62 = <tr>{t60}{t61}</tr>;
    $[108] = t60;
    $[109] = t61;
    $[110] = t62;
  } else {
    t62 = $[110];
  }
  let t63;
  if ($[111] !== formatMessage) {
    t63 = formatMessage("anonymousId");
    $[111] = formatMessage;
    $[112] = t63;
  } else {
    t63 = $[112];
  }
  let t64;
  if ($[113] !== t63) {
    t64 = <th>{t63}</th>;
    $[113] = t63;
    $[114] = t64;
  } else {
    t64 = $[114];
  }
  let t65;
  if ($[115] !== anonymousUserId) {
    t65 = <td>{anonymousUserId}</td>;
    $[115] = anonymousUserId;
    $[116] = t65;
  } else {
    t65 = $[116];
  }
  let t66;
  if ($[117] !== t64 || $[118] !== t65) {
    t66 = <tr>{t64}{t65}</tr>;
    $[117] = t64;
    $[118] = t65;
    $[119] = t66;
  } else {
    t66 = $[119];
  }
  let t67;
  if ($[120] !== copied || $[121] !== formatMessage) {
    t67 = copied ? formatMessage("copied") : formatMessage("copyAll");
    $[120] = copied;
    $[121] = formatMessage;
    $[122] = t67;
  } else {
    t67 = $[122];
  }
  let t68;
  if ($[123] !== copied || $[124] !== handleCopyAll || $[125] !== t67) {
    t68 = <tr><td colSpan={2}><button type="button" onClick={handleCopyAll} disabled={copied}>{t67}</button></td></tr>;
    $[123] = copied;
    $[124] = handleCopyAll;
    $[125] = t67;
    $[126] = t68;
  } else {
    t68 = $[126];
  }
  let t69;
  if ($[127] !== t34 || $[128] !== t38 || $[129] !== t42 || $[130] !== t46 || $[131] !== t50 || $[132] !== t54 || $[133] !== t58 || $[134] !== t62 || $[135] !== t66 || $[136] !== t68) {
    t69 = <tbody>{t34}{t38}{t42}{t46}{t50}{t54}{t58}{t62}{t66}{t68}</tbody>;
    $[127] = t34;
    $[128] = t38;
    $[129] = t42;
    $[130] = t46;
    $[131] = t50;
    $[132] = t54;
    $[133] = t58;
    $[134] = t62;
    $[135] = t66;
    $[136] = t68;
    $[137] = t69;
  } else {
    t69 = $[137];
  }
  let t70;
  if ($[138] !== t30 || $[139] !== t69) {
    t70 = <GlobalErrorDebugBoundary><table>{t30}{t69}</table></GlobalErrorDebugBoundary>;
    $[138] = t30;
    $[139] = t69;
    $[140] = t70;
  } else {
    t70 = $[140];
  }
  let t71;
  if ($[141] !== t25 || $[142] !== t70) {
    t71 = <main className={styles["global-error-debug"]}><details>{t25}{t70}</details></main>;
    $[141] = t25;
    $[142] = t70;
    $[143] = t71;
  } else {
    t71 = $[143];
  }
  return t71;
}
function _temp() {
  return new Date().toString();
}